import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { Fragment, useState } from "react";
import { useFragment } from "react-relay";

import { Box, Text } from "@whoosh/design-system/src/components/primitive";
import { RestrictionFeature } from "@whoosh/features/src/Restrictions/types";

import { EditBookingModal } from "../../../../EditBooking/EditBookingModal";
import { getRequestGroupCount } from "../../../utils/getRequestGroupCount";
import { AddMorePlayersButton } from "../../AddMorePlayersButton/AddMorePlayersButton";
import { useConsolidateNewPlayers } from "../hooks/useConsolidateNewPlayers";
import { ConsolidatedNewPlayers } from "./RoundList/ConsolidatedNewPlayers";
import { Round } from "./RoundList/Round";
import { RoundListAdditionalBookingInfo } from "./RoundList/RoundListAdditionalBookingInfo";
import { getRequestRoundGroups } from "./RoundList/utils/getRequestRoundGroups";
import { RoundList_Booking$key } from "./__generated__/RoundList_Booking.graphql";

type Props = {
  data: RoundList_Booking$key | null;
  viewMore: boolean;
  roundRestrictions: RestrictionFeature.RestrictionBarData[][] | null;
  isDurationBasedFacility: boolean;
  currentSessionMemberShipTypeId: string;
  currentSessionUserClientId: string;
  setViewMore: (value: boolean) => void;
  isLockedRequest: boolean;
  requestGroupCount?: number;
  requestRoundBookingIndexes?: readonly (number | null | undefined)[];
};

export const RoundList = ({
  data,
  viewMore,
  roundRestrictions,
  isDurationBasedFacility,
  currentSessionMemberShipTypeId,
  currentSessionUserClientId,
  setViewMore,
  isLockedRequest,
  requestGroupCount = 1,
  requestRoundBookingIndexes,
}: Props) => {
  const bookingData = useFragment(RoundListFragmentString, data);

  const currentBookingId = bookingData?.id ?? "";

  const bookingsCount = bookingData?.startSlot?.bookings?.count ?? 0;
  const requestRoundEdges = bookingData?.rounds?.edges?.map((edge, index) => {
    if (!edge?.node) return edge;

    return {
      ...edge,
      node: {
        ...edge.node,
        bookingIndex:
          requestRoundBookingIndexes?.[index] ?? edge.node.bookingIndex,
      },
    };
  });
  const effectiveRequestGroupCount = Math.max(
    requestGroupCount,
    getRequestGroupCount(requestRoundEdges?.map((edge) => edge?.node))
  );
  const requestRoundGroups = getRequestRoundGroups(
    requestRoundEdges,
    effectiveRequestGroupCount
  );
  const isAggregateRequest = effectiveRequestGroupCount > 1;

  const shouldConsolidateNewPlayers =
    (bookingData?.rounds?.edges?.length ?? 0) > 4;

  const { unnamedPlayersCount } = useConsolidateNewPlayers({
    bookingFragmentRef: bookingData ?? null,
  });

  const clubSupportsPrivateBookings =
    bookingData?.startSlot?.agenda?.facility?.club?.supportsPrivate ?? false;
  const hidePriceDetails = clubSupportsPrivateBookings;

  const [isEditBookingModalOpen, setIsEditBookingModalOpen] = useState(false);
  const [roundIdToReplace, setRoundIdToReplace] = useState<string | null>(null);

  return (
    <>
      <Box gap={5}>
        {isDurationBasedFacility ? (
          <Box gap={5}>
            <RoundListAdditionalBookingInfo
              bookingFragmentRef={bookingData ?? null}
              viewMore={viewMore}
              setViewMore={setViewMore}
            />
            <Box backgroundColor="outline-default" width="100%" height={1} />
          </Box>
        ) : null}

        <Box>
          <Box gap={1} testID="rounds-list-container">
            {!clubSupportsPrivateBookings && !isAggregateRequest ? (
              <Fragment>
                {bookingData?.rounds?.edges?.map((edge: any, index: number) => {
                  if (!edge?.node) return null;

                  if (
                    shouldConsolidateNewPlayers &&
                    !edge.node.player?.fullName
                  )
                    return null;

                  return (
                    <Round
                      key={edge.node.id}
                      roundRef={edge.node}
                      viewMore={viewMore}
                      hidePriceDetails={hidePriceDetails}
                      roundRestrictions={roundRestrictions?.[index] ?? null}
                      setRoundIdToReplace={setRoundIdToReplace}
                      setIsEditBookingModalOpen={setIsEditBookingModalOpen}
                      setViewMore={setViewMore}
                      canEditBooking={!isLockedRequest}
                    />
                  );
                })}
                {unnamedPlayersCount > 0 && shouldConsolidateNewPlayers && (
                  <ConsolidatedNewPlayers
                    viewMore={viewMore}
                    count={unnamedPlayersCount}
                    roundRef={
                      bookingData?.rounds?.edges?.find(
                        (edge) => !edge?.node?.player?.fullName
                      )?.node ?? null
                    }
                    roundId={
                      bookingData?.rounds?.edges?.find(
                        (edge) => !edge?.node?.player?.fullName
                      )?.node?.id ?? ""
                    }
                  />
                )}
              </Fragment>
            ) : isAggregateRequest ? (
              requestRoundGroups.map(({ groupIndex, rounds }) => (
                <Fragment key={groupIndex}>
                  {groupIndex > 0 ? (
                    <Box
                      height={1}
                      width="100%"
                      marginVertical={3}
                      backgroundColor="surface-dividers"
                      testID={`request-group-divider-${groupIndex}`}
                    />
                  ) : null}
                  <Box gap={1} testID={`request-group-${groupIndex}`}>
                    <Text variant="mainBaseSemiBold" paddingTop={3}>
                      {t`Group ${groupIndex + 1}`}
                    </Text>
                    {rounds.map(({ edge, originalIndex }) =>
                      edge.node ? (
                        <Round
                          key={edge.node.id}
                          roundRef={edge.node}
                          viewMore={viewMore}
                          hidePriceDetails={hidePriceDetails}
                          roundRestrictions={
                            roundRestrictions?.[originalIndex] ?? null
                          }
                          setRoundIdToReplace={setRoundIdToReplace}
                          setIsEditBookingModalOpen={setIsEditBookingModalOpen}
                          setViewMore={setViewMore}
                          canEditBooking={!isLockedRequest}
                        />
                      ) : null
                    )}
                  </Box>
                </Fragment>
              ))
            ) : (
              bookingData?.rounds?.edges?.map((edge, index) => (
                <Round
                  key={edge?.node?.id ?? undefined}
                  roundRef={edge?.node || null}
                  viewMore={viewMore}
                  hidePriceDetails={hidePriceDetails}
                  roundRestrictions={roundRestrictions?.[index] ?? null}
                  setRoundIdToReplace={setRoundIdToReplace}
                  setIsEditBookingModalOpen={setIsEditBookingModalOpen}
                  setViewMore={setViewMore}
                  canEditBooking={!isLockedRequest}
                />
              ))
            )}
          </Box>
          {!isLockedRequest ? (
            <AddMorePlayersButton
              bookingRef={bookingData ?? null}
              currentSessionMemberShipTypeId={currentSessionMemberShipTypeId}
              currentSessionUserClientId={currentSessionUserClientId}
            />
          ) : null}
          {bookingsCount > 1 ? (
            <Box>
              <Text
                height={32}
                variant="mainTiny"
                color="text-secondary"
                alignItems="center"
                style={{ display: "flex" }}
              >{t`OTHER PLAYERS`}</Text>
              <Box>
                {bookingData?.startSlot?.bookings?.edges
                  ?.filter(
                    (bookingEdge) => bookingEdge?.node?.id !== currentBookingId
                  )
                  .map((bookingEdge) => {
                    const booking = bookingEdge?.node;
                    if (!booking?.rounds?.edges?.length) return null;
                    return booking.rounds.edges.map((RoundEdge) => (
                      <Round
                        key={RoundEdge?.node?.id ?? undefined}
                        roundRef={RoundEdge?.node || null}
                        viewMore={false}
                        hidePriceDetails={hidePriceDetails}
                        roundRestrictions={null}
                        setRoundIdToReplace={() => {}}
                        setIsEditBookingModalOpen={() => {}}
                        setViewMore={setViewMore}
                        canEditBooking={false}
                      />
                    ));
                  })}
              </Box>
            </Box>
          ) : null}
        </Box>
      </Box>
      {bookingData && isEditBookingModalOpen && !isLockedRequest ? (
        <EditBookingModal
          isOpen={isEditBookingModalOpen}
          onClose={() => {
            setIsEditBookingModalOpen(false);
          }}
          initialStep={{
            type: "ReplacePlayerPrivate",
            roundId: roundIdToReplace ?? "",
          }}
          bookingRef={bookingData ?? null}
          currentSessionMemberShipTypeId={currentSessionMemberShipTypeId}
          currentSessionUserClientId={currentSessionUserClientId}
        />
      ) : null}
    </>
  );
};

export const RoundListFragmentString = graphql`
  fragment RoundList_Booking on Booking {
    id
    ...RoundListAdditionalBookingInfo_Booking
    ...AddMorePlayersButton_Booking
    ...useConsolidateNewPlayers_Booking
    ...EditBookingModal_Booking
    startSlot {
      ... on TimeSlot {
        bookings {
          count
          edges {
            node {
              id
              rounds {
                edges {
                  node {
                    id
                    ...Round_Round
                  }
                }
              }
            }
          }
        }
      }
      agenda {
        facility {
          club {
            supportsPrivate
          }
        }
      }
    }
    ownerClient {
      ... on Member {
        id
      }
      ... on Customer {
        id
      }
    }
    rounds {
      edges {
        node {
          id
          bookingIndex
          player {
            id
            fullName
          }
          ...Round_Round
          ...ConsolidatedNewPlayers_Round
        }
      }
    }
  }
`;
